import { FC, useEffect, useMemo, useRef, useState } from 'react';
import { withStyles, Card, Table, usePagination } from 'vopay';
import styles from './ContactsPage.module.scss';
import { useHistory, useLocation } from 'react-router-dom';
import { Title, Button, Paginator, TableFiltersContainer } from 'UI';
import { FormValues, useFilterFormConfig } from './useFilterFormConfig';
import useTableConfig from './useTableConfig';
import useContacts from '@/hooks/useApi/useContacts';
import { useAlertDialog } from '@/providers/AlertDialog.provider';
import { useDataFetchIndicator } from '@/hooks';
import { useTranslation } from 'react-i18next';

const cn = withStyles(styles);

const PAGE_SIZE = 10;

const ContactsPage: FC = () => {
  const history = useHistory();
  const [, setPending] = useDataFetchIndicator();
  const { pending, contacts, fetchContacts } = useContacts();
  const tableConfig = useTableConfig(contacts);
  const [total, setTotal] = useState(0);
  const alertDialog = useAlertDialog();
  const overflowRef = useRef<HTMLDivElement>(null);
  const [hasOverflow, setHasOverflow] = useState(false);
  const [atScrollStart, setAtScrollStart] = useState(false);
  const [atScrollEnd, setAtScrollEnd] = useState(false);
  const { t } = useTranslation('translations', { keyPrefix: 'Contacts' });
  const { t: tGlobal } = useTranslation('translations', { keyPrefix: 'Global' });

  useEffect(() => {
    const ref = overflowRef.current;

    if (!ref) return;

    const obs = new MutationObserver(() => {
      setHasOverflow(ref.scrollWidth > ref.clientWidth);
      handleScroll();
    });

    obs.observe(ref, {
      attributes: true,
      childList: true,
      subtree: true,
    });

    const handleScroll = () => {
      setAtScrollStart(ref.scrollLeft < 10);
      setAtScrollEnd(Math.abs(ref.clientWidth + ref.scrollLeft - ref.scrollWidth) < 10);
    };

    ref.addEventListener('scroll', handleScroll);

    return () => {
      obs.disconnect();
      ref.removeEventListener('scroll', handleScroll);
    };
  }, [overflowRef.current]);

  const { pageCount, pageIndex, setPageIndex } = usePagination(total, {
    pageSize: PAGE_SIZE,
  });

  const { query, setQuery, filtersApplied } = useQuery();

  const { renderFormElements, ctx } = useFilterFormConfig({
    onSubmit: (values) => {
      setQuery(values);
    },
  });

  useEffect(() => {
    setPending(pending);
  }, [pending]);

  useEffect(() => {
    ctx.setValues(
      Object.entries(query).reduce((acc, [field, val]) => {
        return {
          ...acc,
          [field]: val,
        };
      }, ctx.values),
    );

    setPageIndex(0);
    refresh(0);
  }, [query]);

  async function refresh(offset: number) {
    try {
      const res = await fetchContacts({
        Offset: offset,
        Limit: PAGE_SIZE,
        SearchText: query.keyword,
        IsBusiness: query.clientType ? query.clientType === 'business' : undefined,
        Label: query.label,
        AccountStatus: query.accountStatus ? query.accountStatus === 'active' : undefined,
      });

      setTotal(res.PaginationMetaData.TotalRecords);
    } catch (err) {
      console.error(err);

      if (err instanceof Error) {
        alertDialog.error(
          t('error', {
            defaultValue: 'Error',
          }),
          err.message,
          {
            abortLabel: tGlobal('Buttons.close', {
              defaultValue: 'Close',
            }),
          },
        );
      }
    }
  }

  return (
    <div className={cn('contactsPage')}>
      <header className={cn('pageHeader')}>
        <Title>
          {t('pageTitle', {
            defaultValue: 'Contacts',
          })}
        </Title>

        <Button colorScheme="primary" onClick={() => history.push('/contacts/create', { showBackButton: true })}>
          {t('addContact', {
            defaultValue: 'Add Contact',
          })}
        </Button>
      </header>

      <Card
        style={{
          padding: '16px',
        }}
      >
        <TableFiltersContainer filtersApplied={filtersApplied}>
          <TableFiltersContainer.Trigger />

          <TableFiltersContainer.Content>
            <form className={cn('filtersInner')} onSubmit={ctx.handleSubmit}>
              {renderFormElements()}

              <Button type="submit" colorScheme="primary" size="large">
                {tGlobal('Buttons.search', {
                  defaultValue: 'Search',
                })}
              </Button>
            </form>
          </TableFiltersContainer.Content>
        </TableFiltersContainer>

        <div
          className={cn(
            'tableContainerWithFade',
            hasOverflow && 'hasOverflow',
            atScrollStart && 'atScrollStart',
            atScrollEnd && 'atScrollEnd',
          )}
        >
          <div ref={overflowRef} className={cn('tableOverflowContainer')}>
            <Table version="2" {...tableConfig} />
          </div>
        </div>

        {pageCount > 1 && (
          <div className={cn('paginator')}>
            <Paginator
              pageCount={pageCount}
              onPageChange={(e) => {
                const newOffset = setPageIndex(e.selected);
                refresh(newOffset);
              }}
              pageOffset={pageIndex}
            />
          </div>
        )}
      </Card>
    </div>
  );
};

export default ContactsPage;

function useQuery() {
  const { search } = useLocation();
  const history = useHistory();

  const query = useMemo<FormValues>(() => {
    return Object.fromEntries(new URLSearchParams(search)) as FormValues;
  }, [search]);

  const filtersApplied = useMemo(() => Object.keys(query).length > 0, [query]);

  function setQuery(data: FormValues) {
    const newParams = new URLSearchParams(
      Object.entries(data).reduce((acc, [key, val]) => {
        if (!val) return acc;

        return {
          ...acc,
          [key]: val,
        };
      }, {}),
    );

    history.replace({ pathname: location.pathname, search: newParams.toString() });
  }

  return {
    query,
    setQuery,
    filtersApplied,
  };
}
